import { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { getCards, getBrands } from '@/api/products';
import CardItemView from '@/components/CardItemView';
import Select from '@/components/Select';
import Sort from '@/components/Sort';
import Breadcrumb from '@/components/Breadcrumb';
import Checkbox from '@/components/Checkbox';
import Button from '@/components/Button';
import { SearchResultsSkeleton, SearchRecommendedCardsSkeleton, CardSkeleton } from '@/components/Skeleton';
import useMinimumLoadingTime from '@/hooks/useMinimumLoadingTime';

const SearchPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = searchParams.get('q') ?? '';

  // Filters/state
  const [selectedBrand, setSelectedBrand] = useState('ALL');
  const [selectedCategory, setSelectedCategory] = useState('ALL');
  const [sortConfig, setSortConfig] = useState({ field: 'POPULARITY', direction: 'DESC' });
  const [searchByItem, setSearchByItem] = useState(false);

  // Track whether the initial search for the current query finished
  const [hasLoadedInitialSearch, setHasLoadedInitialSearch] = useState(false);

  // reset when ?q= changes
  useEffect(() => {
    setSelectedBrand('ALL');
    setSelectedCategory('ALL');
    setSortConfig({ field: 'POPULARITY', direction: 'DESC' });
    setSearchByItem(false);
    setHasLoadedInitialSearch(false);
  }, [query]);

  // Brands
  const { data: brandsData, isLoading: isBrandsLoading } = useQuery({
    queryKey: ['brands'],
    queryFn: getBrands,
    select: data => data.data,
  });

  // Search results (switched to useInfiniteQuery)
  const {
    data: searchPages,
    isLoading,           // first fetch for this queryKey
    isFetching,          // any fetch (first + refetch + pagination)
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['searchCards', query, selectedCategory, sortConfig, selectedBrand, searchByItem],
    queryFn: ({ pageParam }) =>
      getCards({
        ...(selectedCategory !== 'ALL' && { type: selectedCategory }),
        sort: sortConfig.field,
        direction: sortConfig.direction,
        ...(selectedBrand !== 'ALL' && { brandId: selectedBrand }),
        limit: 40,
        keyword: query,
        cursor: pageParam ?? null,
        searchByItem,
      }),
    enabled: query.length > 0,
    initialPageParam: null,
    getNextPageParam: lastPage => lastPage?.cursor ?? undefined,
  });

  // mark initial search done
  useEffect(() => {
    if (searchPages !== undefined && !hasLoadedInitialSearch) {
      setHasLoadedInitialSearch(true);
    }
  }, [searchPages, hasLoadedInitialSearch]);

  // Recommended (for empty state)
  const {
    data: recommendedCards,
    isLoading: isRecommendedLoading,
  } = useQuery({
    queryKey: ['recommendedCards', 'SearchPage'],
    queryFn: () =>
      getCards({
        type: 'SINGLE_CARD',
        sort: 'POPULARITY',
        direction: 'DESC',
        limit: 20,
      }),
  });

  // Handlers — no manual accumulation/clearing needed with useInfiniteQuery
  const handleBrandChange = brandId => {
    setSelectedBrand(brandId);
  };

  const handleCategoryChange = category => {
    setSelectedCategory(category);
  };

  const handleSortChange = (sortItem, direction) => {
    const sortMapping = {
      date: 'RELEASE_DATE',
      price: 'LOWEST_PRICE',
      popularity: 'POPULARITY',
    };
    setSortConfig({
      field: sortMapping[sortItem.value],
      direction: direction.toUpperCase(),
    });
  };

  const handleSearchByItemChange = checked => {
    setSearchByItem(checked);
  };

  // Derived
  const displayCards = searchPages?.pages?.flatMap(p => p?.data || []) || [];
  const resultsCount = displayCards.length;
  const hasResults = resultsCount > 0;

  // Load more
  const handleLoadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  // Track pages loaded for auto load more
  const pagesLoaded = searchPages?.pages?.length || 0;

  // Infinite scroll (auto up to 3 pages, then show button)
  const handleScroll = useCallback(() => {
    if (isFetchingNextPage || !hasNextPage || !hasResults || pagesLoaded >= 1) return;

    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const windowHeight = window.innerHeight;
    const docHeight = document.documentElement.scrollHeight;

    if (scrollTop + windowHeight >= docHeight - 200) {
      handleLoadMore();
    }
  }, [isFetchingNextPage, hasNextPage, hasResults, pagesLoaded]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Check scroll position after loading completes
  useEffect(() => {
    if (!isFetchingNextPage && hasNextPage && hasResults && pagesLoaded < 3) {
      // Small delay to ensure DOM is updated
      const timer = setTimeout(() => {
        handleScroll();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isFetchingNextPage, hasNextPage, hasResults, pagesLoaded, handleScroll]);

  // ---------- SKELETON STATE ----------
  // Full-screen skeleton only for the very first fetch of current ?q=
  const isInitialQueryLoading = query.length > 0 && !hasLoadedInitialSearch && isLoading;
  // List skeleton for refetches (filters/sort) on page 1 only (not during fetchNextPage)
  const isRefetchingFirstPage = hasLoadedInitialSearch && isFetching && !isFetchingNextPage;

  const showFullScreenSkeleton = useMinimumLoadingTime(isInitialQueryLoading);
  const showListSkeleton = useMinimumLoadingTime(isRefetchingFirstPage);
  const showRecommendedLoading = useMinimumLoadingTime(isRecommendedLoading);

  // Show heading/filters after the true first load finishes (don’t hide on refetch)
  const showSearchUI = query && !showFullScreenSkeleton && (hasResults || (selectedBrand !== 'ALL' || selectedCategory !== 'ALL' || searchByItem));
  // -----------------------------------

  // Options
  const brandOptions = [
    { value: 'ALL', label: t('home.filters.brands.all') },
    ...(brandsData?.map(brand => ({
      value: brand.id,
      label: brand.localizedName,
    })) || []),
  ];

  const categoryOptions = [
    { value: 'ALL', label: t('home.filters.categories.all') },
    { value: 'SINGLE_CARD', label: t('home.filters.categories.single') },
    { value: 'CARD_BOX', label: t('home.filters.categories.box') },
  ];

  const breadcrumbItems = [
    { label: t('common.home'), href: '/' },
    { label: t('common.search') },
  ];

  return (
    <div className="container py-6">
      {/* Breadcrumbs */}
      <div className="py-6">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Heading — stays visible on refetch */}
      {showSearchUI && (
        <div className="pt-6 pb-12 flex flex-col gap-[7px]">
          <h1 className="text-[#111111] text-2xl font-bold">"{query}" {t('search.results_title')}</h1>
        </div>
      )}

      {/* Filters — stay visible on refetch */}
      {showSearchUI && (
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-4">
            <Select
              value={selectedCategory}
              onValueChange={handleCategoryChange}
              options={categoryOptions}
              className="w-48"
            />
            <Select
              value={selectedBrand}
              onValueChange={handleBrandChange}
              options={brandOptions}
              className="w-48"
              disabled={isBrandsLoading && !brandsData && !hasLoadedInitialSearch}
            />
          </div>

          <div className="flex items-center gap-4">
            {/* <div className="bg-[#f4f4f4] rounded-[18px] px-[18px] py-2 flex items-center">
              <div className="flex items-center gap-1.5">
                <Checkbox
                  checked={searchByItem}
                  onCheckedChange={handleSearchByItemChange}
                />
                <div className="text-[#111111] text-sm font-normal">
                  {t('search.card_name_only')}
                </div>
              </div>
            </div> */}
            <Sort
              items={[
                { label: t('home.filters.sort.release_date'), value: 'date' },
                { label: t('home.filters.sort.price'), value: 'price' },
                { label: t('home.filters.sort.popularity'), value: 'popularity' },
              ]}
              onChange={handleSortChange}
              defaultSortIndex={2}
              defaultDirection="desc"
            />
          </div>
        </div>
      )}

      {/* Results & skeletons */}
      {showFullScreenSkeleton ? (
        <SearchResultsSkeleton />
      ) : showListSkeleton ? (
        // Show only the list skeleton during refetch; heading/filters stay visible above
        <div className="grid grid-cols-5 max:grid-cols-6 gap-6">
          {Array.from({ length: 40 }, (_, i) => (
            <CardSkeleton key={`filter-skeleton-${i}`} />
          ))}
        </div>
      ) : (
        <>
          {hasResults ? (
            <>
              <div className="grid grid-cols-5 max:grid-cols-6 gap-6">
                {displayCards.map(card => (
                  <CardItemView
                    key={card.id}
                    id={card.id}
                    type={card.type === 'SINGLE_CARD' ? 'PSA_SINGLE_CARD' : 'CARD_BOX'}
                    name={card.localizedRawName}
                    image={card.images?.[0]?.url}
                    brand={card?.brand?.localizedName}
                    price={(() => {
                      const vals = [
                        card.stats?.lowestPriceOnSale,
                        card.stats?.lowestPriceOnSaleOfficial,
                      ].filter(v => typeof v === 'number' && v > 0);
                      return vals.length ? Math.min(...vals) : 0;
                    })()}
                    orders={card.stats?.salesVolume}
                    code={card.code}
                    rawCode={card.rawCode}
                    set={card.set}
                    rarityInfo={card.rarityInfo}
                  />
                ))}

                {/* Inline skeletons for pagination */}
                {isFetchingNextPage && Array.from({ length: 40 }, (_, i) => (
                  <CardSkeleton key={`skeleton-${i}`} />
                ))}
              </div>

              {/* Show load more button after 3 pages */}
              {pagesLoaded >= 1 && hasNextPage && !isFetchingNextPage && (
                <div className="flex justify-center mt-8">
                  <Button
                    onClick={handleLoadMore}
                    variant="default"
                    size="l"
                  >
                    {t('home.actions.load_more')}
                  </Button>
                </div>
              )}
            </>
          ) : (
            <>
              {query && (selectedBrand !== 'ALL' || selectedCategory !== 'ALL' || searchByItem) ? (
                <div className="py-8 text-center text-[#aaaaaa] text-sm">
                  {t('search.no_results_with_filters')}
                </div>
              ) : (
                <>
                  <div className="pt-6 pb-4 bg-white rounded-md inline-flex flex-col justify-start items-start gap-[7px]">
                    <div className="text-[#111111] text-2xl font-bold">
                      {t('search.no_results_title', { query })}
                    </div>
                    <div>
                      <span className="text-[#aaaaaa] text-sm font-normal">
                        {t('search.no_results_message')}
                      </span>
                    </div>
                  </div>

                  {/* Recommended Cards */}
                  {showRecommendedLoading ? (
                    <SearchRecommendedCardsSkeleton />
                  ) : recommendedCards?.data?.length > 0 ? (
                    <div className="mt-8">
                      <h2 className="text-xl font-bold mb-6">{t('home.sections.recommended_cards')}</h2>
                      <div className="grid grid-cols-5 gap-6">
                        {recommendedCards.data.map(card => (
                          <CardItemView
                            key={card.id}
                            id={card.id}
                            type={card.type === 'SINGLE_CARD' ? 'PSA_SINGLE_CARD' : 'CARD_BOX'}
                            name={card.localizedRawName}
                            image={card.images?.[0]?.url}
                            brand={card?.brand?.localizedName}
                            price={(() => {
                              const vals = [
                                card.stats?.lowestPriceOnSale,
                                card.stats?.lowestPriceOnSaleOfficial,
                              ].filter(v => typeof v === 'number' && v > 0);
                              return vals.length ? Math.min(...vals) : 0;
                            })()}
                            orders={card.stats?.salesVolume}
                            code={card.code}
                            rawCode={card.rawCode}
                            set={card.set}
                            rarityInfo={card.rarityInfo}
                          />
                        ))}
                      </div>
                    </div>
                  ) : null}
                </>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default SearchPage;